import type { CharacterIndexReaderResumeTarget } from './characterIndexProgress'

export interface ParsedLedgerStudioPathRoute {
  bookId: string
  chapterId: string
}

export interface BuildLedgerStudioPathInput {
  bookId: string
  chapterId: string
  readerResume?: CharacterIndexReaderResumeTarget
}

export interface ParsedLedgerStudioSearch {
  readerResume: CharacterIndexReaderResumeTarget | null
}

export const buildCameraLucidaPath = ({ bookId, chapterId }: ParsedLedgerStudioPathRoute): string => (
  `${buildLedgerStudioPath({ bookId, chapterId })}?view=camera`
)

export const isCameraLucidaSearch = (search: string): boolean => (
  new URLSearchParams(search).get('view') === 'camera'
)

export const buildLedgerStudioPath = ({ bookId, chapterId, readerResume }: BuildLedgerStudioPathInput): string => {
  const pathname = `/books/${bookId}/chapters/${chapterId}/ledger`
  if (!readerResume) {
    return pathname
  }
  const search = new URLSearchParams({
    view: 'reader',
    reader: 'character-index',
    book: readerResume.bookId,
    chapter: readerResume.chapterId,
    witness: readerResume.witnessId,
    evidence: readerResume.evidenceKind,
  })
  if (readerResume.rowId) search.set('row', readerResume.rowId)
  if (readerResume.unitLabel) search.set('unit', readerResume.unitLabel)
  if (readerResume.characterId) search.set('character', readerResume.characterId)
  if (readerResume.evidenceId) search.set('item', readerResume.evidenceId)
  return `${pathname}?${search.toString()}`
}

export const parseLedgerStudioSearch = (search: string): ParsedLedgerStudioSearch => {
  const params = new URLSearchParams(search)
  if (params.get('view') !== 'reader' || params.get('reader') !== 'character-index') {
    return { readerResume: null }
  }
  const bookId = params.get('book') || ''
  const chapterId = params.get('chapter') || ''
  const witnessId = params.get('witness') || ''
  const evidence = params.get('evidence')
  if (
    !bookId
    || !chapterId
    || !witnessId
    || (evidence !== 'cast'
      && evidence !== 'coreference'
      && evidence !== 'focalization'
      && evidence !== 'characterization')
  ) {
    return { readerResume: null }
  }
  return {
    readerResume: {
      kind: 'reader-character-index',
      bookId,
      chapterId,
      witnessId,
      ...(params.get('row') ? { rowId: params.get('row')! } : {}),
      ...(params.get('unit') ? { unitLabel: params.get('unit')! } : {}),
      ...(params.get('character') ? { characterId: params.get('character')! } : {}),
      ...(params.get('item') ? { evidenceId: params.get('item')! } : {}),
      evidenceKind: evidence,
    },
  }
}

export const parseLedgerStudioPath = (pathname: string): ParsedLedgerStudioPathRoute | null => {
  const segments = (pathname || '').split('/').filter(Boolean)

  if (segments.length === 5 && segments[0] === 'books' && segments[2] === 'chapters' && segments[4] === 'ledger') {
    return {
      bookId: segments[1],
      chapterId: segments[3]
    }
  }

  return null
}
